import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { borderRadius, Col, Cols, Div, fontSizing, spacing, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';

interface IProps {
  name: string;
  handleCloseEditor: (text: string) => void;
  handleOpenEditor?: () => void;
  placeholder?: string;
  disabled?: boolean;
  small?: boolean;
  large?: boolean;
}

export const TextEditor = ({
  name,
  placeholder,
  handleCloseEditor,
  handleOpenEditor,
  disabled,
  small,
  large,
}: IProps) => {
  const inputRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(name);

  const getInputTextSize = () => {
    switch (true) {
      case small:
        return fontSizing.extraSmall;
      case large:
        return fontSizing.base;
      default:
        return fontSizing.small;
    }
  };

  const onOpenEditor = () => {
    unstable_batchedUpdates(() => {
      setIsEditing(true);
      handleOpenEditor && handleOpenEditor();
    });
  };

  const onClickOutside = (e: any) => {
    // @ts-ignore // overrides typescript false null
    if (inputRef.current && !inputRef.current.contains(e.target)) {
      unstable_batchedUpdates(() => {
        handleCloseEditor(text);
        setIsEditing(false);
      });
    }
  };

  const onChange = (value: string) => {
    setText(value);
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const clipboardData = e.clipboardData?.getData('Text') || '';
    onChange(clipboardData);
  };

  useEffect(() => {
    if (isEditing) {
      document.addEventListener('mousedown', onClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
    };
  });

  useEffect(() => {
    !disabled && setText(name);
  }, [name]);

  return (
    <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
      {isEditing && !disabled ? (
        <Col flex>
          <input
            ref={inputRef}
            className='name-editor'
            autoFocus={true}
            value={text}
            placeholder={placeholder}
            onChange={(e: React.FormEvent<HTMLInputElement>) => {
              e.stopPropagation();
              e.preventDefault();
              onChange(e.currentTarget.value);
            }}
            onPaste={onPaste}
            maxLength={99}
            minLength={3}
            style={{
              background: publicaColors.white,
              border: `0.5px solid ${publicaColors.lightGray}`,
              borderRadius: borderRadius.small,
              boxShadow: 'inset 1px 2px 8px rgba(0, 0, 0, 0.05)',
              fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
              padding: '6px 12px',
              width: '100%',
              fontSize: getInputTextSize(),
            }}
          />
        </Col>
      ) : (
        <Col>
          <Div {...(!disabled && { onClick: () => onOpenEditor() })}>
            <Text small={small} large={large} bold>
              {text}
            </Text>
          </Div>
        </Col>
      )}
      {!isEditing && !disabled && (
        <Col displayFlex onClick={onOpenEditor} style={{ cursor: 'pointer' }}>
          <IconV2 name='edit' large />
        </Col>
      )}
    </Cols>
  );
};
